/**
 * Farlo UI | Selectbox
 *
 */

class FarloUiSelectBox {
  constructor(element) {
    if (element && element.multiple) {
      return;
    }
    this.element = element;

    this.selectMutationObserver = null;
    this.documentClickHandler = null;
    this.keydownHandler = null;
    this.focusHandler = null;
    this.blurHandler = null;
    this.changeHandler = null; // NEW

    this.initialiseSelect(this.element);
  }

  initialiseSelect(selectBox) {
    if (selectBox.parentNode && selectBox.parentNode.classList && selectBox.parentNode.classList.contains('farlo-ui-selectbox')) {
      return;
    }

    selectBox.classList.add('farlo-ui-selectbox__native');

    const selectField = this.createSelectFieldElement(selectBox);
    const selectDropdown = selectField.querySelector('.farlo-ui-selectbox__dropdown');

    selectDropdown.innerHTML = this.createSelectOptions(selectBox);

    this.setupEvents(selectBox, selectField, selectDropdown);

    this.observeSelectChanges(selectBox, () => this.refreshSelect(selectBox, selectField, selectDropdown));

    const initial = selectDropdown.querySelector(`.farlo-ui-selectbox__option[data-index="${ selectBox.selectedIndex }"]`)
      || selectDropdown.querySelector('.farlo-ui-selectbox__option--is-selected');
    this.updateVisibleSelection(selectBox, initial);
  }

  removeSelect(selectBox) {
    const selectField = selectBox.parentNode;
    if (selectField && selectField.classList.contains('farlo-ui-selectbox')) {
      if (this.documentClickHandler) {
        document.removeEventListener('click', this.documentClickHandler);
      }
      if (this.keydownHandler) {
        selectField.removeEventListener('keydown', this.keydownHandler);
      }
      if (this.focusHandler) {
        selectBox.removeEventListener('focus', this.focusHandler);
      }
      if (this.blurHandler) {
        selectBox.removeEventListener('blur', this.blurHandler);
      }
      if (this.changeHandler) { // NEW
        selectBox.removeEventListener('change', this.changeHandler);
      }

      if (this.selectMutationObserver) {
        this.selectMutationObserver.disconnect();
      }

      selectBox.classList.remove('farlo-ui-selectbox__native');
      selectField.parentNode.insertBefore(selectBox, selectField);
      selectField.parentNode.removeChild(selectField);
    }
  }

  createSelectFieldElement(selectBox) {
    const selectField = document.createElement('div');
    selectField.classList.add('farlo-ui-selectbox');
    if (selectBox.disabled) {
      selectField.classList.add('farlo-ui-selectbox--disabled');
    }
    selectField.setAttribute('tabindex', '-1');

    selectField.innerHTML = `
      <div class="farlo-ui-selectbox__simulated" aria-hidden="true"></div>
      <div class="farlo-ui-selectbox__dropdown" role="listbox"></div>
    `;

    selectBox.parentNode.insertBefore(selectField, selectBox);
    selectField.appendChild(selectBox);

    return selectField;
  }

  createSelectOptions(selectBox) {
    let optionHtml = '';
    let index = 0;

    if (selectBox.children.length) {
      Array.from(selectBox.children).forEach((child) => {
        if (child.tagName === 'OPTGROUP') {
          const groupDisabled = child.disabled;
          const groupLabel = child.getAttribute('label') || '';
          optionHtml += `
            <div class="farlo-ui-selectbox__optgroup" role="group" aria-label="${ this.escapeHtml(groupLabel) }">
              <div class="farlo-ui-selectbox__optgroup-heading">${ this.escapeHtml(groupLabel) }</div>
          `;
          Array.from(child.children).forEach((option) => {
            optionHtml += this.createOptionMarkup(selectBox, option, index++, groupDisabled);
          });
          optionHtml += `</div>`;
        } else if (child.tagName === 'OPTION') {
          optionHtml += this.createOptionMarkup(selectBox, child, index++, false);
        }
      });
    }

    return optionHtml;
  }

  createOptionMarkup(selectBox, option, fallbackIndex, parentDisabled) {
    const isDisabled =
      parentDisabled ||
      option.disabled ||
      (option.parentElement.tagName === 'OPTGROUP' && option.parentElement.disabled);

    const index = Array.prototype.indexOf.call(selectBox.options, option);
    const safeIndex = index >= 0 ? index : fallbackIndex;

    const isSelected = option.selected;
    const classes = [
      'farlo-ui-selectbox__option',
      isDisabled ? 'farlo-ui-selectbox__option--is-disabled' : '',
      isSelected ? 'farlo-ui-selectbox__option--is-selected' : ''
    ].filter(Boolean).join(' ');

    const attrs = [
      `class="${ classes }"`,
      !isDisabled ? 'tabindex="0"' : '',
      'role="option"',
      `data-index="${ String(safeIndex) }"`,
      isSelected ? 'aria-selected="true"' : 'aria-selected="false"'
    ].filter(Boolean).join(' ');

    return `<div ${ attrs }>${ this.escapeHtml(option.textContent || '') }</div>`;
  }

  setupEvents(selectBox, selectField, selectDropdown) {
    const selectBoxSimulated = selectField.querySelector('.farlo-ui-selectbox__simulated');

    selectBoxSimulated.addEventListener('click', () => {
      this.toggleDropdown(selectField, selectDropdown);
    });

    this.bindOptionClickHandlers(selectBox, selectDropdown);

    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
    }
    this.documentClickHandler = (event) => {
      if (!selectField.contains(event.target)) {
        this.hideDropdown(selectField, selectDropdown);
      }
    };
    document.addEventListener('click', this.documentClickHandler);

    if (this.focusHandler) selectBox.removeEventListener('focus', this.focusHandler);
    if (this.blurHandler)  selectBox.removeEventListener('blur', this.blurHandler);

    this.focusHandler = () => selectField.classList.add('farlo-ui-selectbox--is-focused');
    this.blurHandler  = () => selectField.classList.remove('farlo-ui-selectbox--is-focused');

    selectBox.addEventListener('focus', this.focusHandler);
    selectBox.addEventListener('blur', this.blurHandler);

    if (this.keydownHandler) selectField.removeEventListener('keydown', this.keydownHandler);
    this.keydownHandler = (event) => this.handleKeyboardNavigation(event, selectBox, selectField, selectDropdown);
    selectField.addEventListener('keydown', this.keydownHandler);
    if (this.changeHandler) {
      selectBox.removeEventListener('change', this.changeHandler);
    }
    this.changeHandler = () => {
      this.updateSelectedOption(selectDropdown, selectBox.selectedIndex);
      const current = selectDropdown.querySelector(
        `.farlo-ui-selectbox__option[data-index="\${ selectBox.selectedIndex }"]`
      );
      this.updateVisibleSelection(selectBox, current);
    };
    selectBox.addEventListener('change', this.changeHandler);
  }

  bindOptionClickHandlers(selectBox, selectDropdown) {
    const selectBoxOptions = selectDropdown.getElementsByClassName('farlo-ui-selectbox__option');
    Array.from(selectBoxOptions).forEach((optionItem) => {
      optionItem.addEventListener('click', () => {
        if (!optionItem.classList.contains('farlo-ui-selectbox__option--is-disabled')) {
          this.selectOption(selectBox, optionItem, selectDropdown);
        }
      });
    });
  }

  toggleDropdown(selectField, selectDropdown) {
    const isActive = selectField.classList.contains('farlo-ui-selectbox--is-active');
    if (isActive) {
      this.hideDropdown(selectField, selectDropdown);
    } else {
      this.showDropdown(selectField, selectDropdown);
    }
  }

  showDropdown(selectField, selectDropdown) {
    Array.from(selectDropdown.querySelectorAll('.farlo-ui-selectbox__option--is-selected'))
      .forEach((el) => el.classList.remove('farlo-ui-selectbox__option--is-selected'));

    const current = selectDropdown.querySelector(`.farlo-ui-selectbox__option[data-index="${ this.element.selectedIndex }"]`);
    if (current) current.classList.add('farlo-ui-selectbox__option--is-selected');

    selectField.classList.add('farlo-ui-selectbox--is-active');

    const dropdownRect = selectDropdown.getBoundingClientRect();
    const viewportBottom = document.documentElement.clientHeight + window.scrollY;

    if (dropdownRect.top + selectDropdown.clientHeight > viewportBottom) {
      if (dropdownRect.top > viewportBottom - dropdownRect.top) {
        selectDropdown.style.maxHeight = (dropdownRect.top * 0.8) + 'px';
        selectField.classList.add('farlo-ui-selectbox--is-above');
      } else {
        selectDropdown.style.maxHeight = ((document.documentElement.clientHeight - dropdownRect.top) * 0.8) + 'px';
        selectField.classList.remove('farlo-ui-selectbox--is-above');
      }
    } else {
      selectDropdown.style.maxHeight = '';
      selectField.classList.remove('farlo-ui-selectbox--is-above');
    }

    selectDropdown.style.display = 'block';
  }

  hideDropdown(selectField, selectDropdown) {
    selectField.classList.remove('farlo-ui-selectbox--is-active');
    selectDropdown.style.display = 'none';
    selectField.classList.remove('farlo-ui-selectbox--is-above');
    selectDropdown.style.maxHeight = '';
  }

  selectOption(selectBox, selectboxOption, selectDropdown) {
    const index = parseInt(selectboxOption.getAttribute('data-index'), 10);
    const selectField = selectBox.parentNode;

    selectBox.selectedIndex = index;
    selectBox.dispatchEvent(new Event('change', { bubbles: true }));

    this.updateSelectedOption(selectDropdown, index);
    this.updateVisibleSelection(selectBox, selectboxOption);

    this.hideDropdown(selectField, selectDropdown);
  }

  updateVisibleSelection(selectBox, selectboxOption) {
    const simulated = selectBox.parentNode.querySelector('.farlo-ui-selectbox__simulated');
    if (!simulated) return;

    if (!selectboxOption) {
      const opt = selectBox.options[selectBox.selectedIndex];
      simulated.textContent = opt ? opt.textContent : '';
      return;
    }

    simulated.textContent = selectboxOption.textContent || '';
  }

  updateSelectedOption(selectDropdown, selectedIndex) {
    const prev = selectDropdown.querySelector('.farlo-ui-selectbox__option--is-selected');
    if (prev) {
      prev.classList.remove('farlo-ui-selectbox__option--is-selected');
      prev.setAttribute('aria-selected', 'false');
    }
    const next = selectDropdown.querySelector(`.farlo-ui-selectbox__option[data-index="${ selectedIndex }"]`);
    if (next) {
      next.classList.add('farlo-ui-selectbox__option--is-selected');
      next.setAttribute('aria-selected', 'true');
    }
  }

  handleKeyboardNavigation(event, selectBox, selectField, selectDropdown) {
    const key = event.key;
    const isActive = selectField.classList.contains('farlo-ui-selectbox--is-active');
    const options = Array.from(selectDropdown.getElementsByClassName('farlo-ui-selectbox__option'))
      .filter((el) => !el.classList.contains('farlo-ui-selectbox__option--is-disabled'));
    const selected = selectDropdown.querySelector('.farlo-ui-selectbox__option--is-selected');
    let newIndex;

    switch (key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!isActive) {
          this.showDropdown(selectField, selectDropdown);
        } else if (selected) {
          const idx = options.indexOf(selected);
          newIndex = (idx + 1) % options.length;
          this.highlightOption(options, newIndex);
        } else {
          this.highlightOption(options, 0);
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (!isActive) {
          this.showDropdown(selectField, selectDropdown);
        } else if (selected) {
          const idx = options.indexOf(selected);
          newIndex = (idx - 1 + options.length) % options.length;
          this.highlightOption(options, newIndex);
        } else {
          this.highlightOption(options, options.length - 1);
        }
        break;

      case 'Enter':
        if (isActive && selected) {
          event.preventDefault();
          this.selectOption(selectBox, selected, selectDropdown);
        }
        break;

      case 'Escape':
        if (isActive) {
          event.preventDefault();
          this.hideDropdown(selectField, selectDropdown);
        }
        break;

      case 'Tab':
        this.hideDropdown(selectField, selectDropdown);
        break;
    }
  }

  highlightOption(optionEls, index) {
    optionEls.forEach((el) => el.classList.remove('farlo-ui-selectbox__option--is-selected'));
    const target = optionEls[index];
    if (target) {
      target.classList.add('farlo-ui-selectbox__option--is-selected');
      target.scrollIntoView({ block: 'nearest' });
    }
  }

  observeSelectChanges(selectBox, callback) {
    this.selectMutationObserver = new MutationObserver(callback);
    this.selectMutationObserver.observe(selectBox, {
      childList: true,
      subtree: false,
      attributes: false,
      characterData: false
    });
  }

  refreshSelect(selectBox, selectField, selectDropdown) {
    selectDropdown.innerHTML = this.createSelectOptions(selectBox);
    selectField.classList.toggle('farlo-ui-selectbox--disabled', selectBox.disabled);

    this.bindOptionClickHandlers(selectBox, selectDropdown);

    const current = selectDropdown.querySelector(`.farlo-ui-selectbox__option[data-index="${ selectBox.selectedIndex }"]`);
    this.updateVisibleSelection(selectBox, current);
  }

  escapeHtml(str) {
    return String(str)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

export default FarloUiSelectBox;
